import {
  toLocaleDateTime,
  toLocaleNumber,
} from '@chr/online-shared-ui-localizations';
import { parseISO } from 'date-fns';
import { Job, Location, Status, StopType } from '../apis/dtos';

// date helper functions
export declare type Stringable =
  | string
  | number
  | boolean
  | { toString(): string };

const intlDateOptions: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

const intlTimeOptions: Intl.DateTimeFormatOptions = { timeStyle: 'short' };

function parseToISODate(dateTime: string) {
  const dateTimeWithoutOffset = dateTime.substring(0, 19);

  const newDate = new Date(dateTimeWithoutOffset);
  const ISODateString = newDate.toISOString();
  return parseISO(ISODateString);
}

export const IsoToDate = (
  isoDateString: string | null | undefined,
  defaultDate = '-',
  dateOptions: Intl.DateTimeFormatOptions = intlDateOptions
): string => {
  if (isoDateString && isoDateString.length > 1) {
    const date = parseToISODate(isoDateString);
    return toLocaleDateTime(date, dateOptions);
  }

  return defaultDate;
};

export const IsoToTime = (
  isoDateTimeString: string | null | undefined
): string => {
  let formattedTime = '-';

  if (isoDateTimeString && isoDateTimeString.length > 1) {
    const date = parseToISODate(isoDateTimeString);
    formattedTime = toLocaleDateTime(date, intlTimeOptions).replace(
      ' ',
      '\u00A0'
    );
  }

  return formattedTime;
};

export const IsoToDateAtTime = (
  isoDate: string | undefined,
  translate: (key: string, replaceValues: Stringable[]) => string,
  defaultDateTime = '-'
): string => {
  const convertedDate: string = isoDate ? IsoToDate(isoDate) : '';
  const convertedTime: string = isoDate ? IsoToTime(isoDate) : '';

  return convertedDate.length > 1 && convertedTime.length > 1
    ? translate('{0} at {1}', [convertedDate, convertedTime])
    : defaultDateTime;
};

export const FormatLocationWithProperty = (
  city?: string,
  state?: string,
  country?: string
): string => {
  if (!city && !state) {
    return '';
  }

  if (state === country) {
    return [city, country].filter((n) => n).join(', ');
  }

  return [city, state, country].filter((n) => n).join(', ');
};

export const FormatLocation = (location?: Location): string => {
  if (!location) {
    return '';
  }

  if (location.state === location.country) {
    return [location.city, location.country].filter((n) => n).join(', ');
  }

  return [location.city, location.state, location.country]
    .filter((n) => n)
    .join(', ');
};

export const RouteStatus = (
  shipment: Job,
  translate: (key: string, replaceValues?: Stringable[]) => string
): string => {
  switch (shipment.status) {
    case Status.InTransit: {
      // find first stop without an arrived value
      const seqNumSorted = [...shipment.stops].sort((s1, s2) => {
        return s1.sequenceNumber - s2.sequenceNumber;
      });
      const nextLocation = seqNumSorted.find((s) => !s.arrived)?.location;

      if (nextLocation) {
        return `${translate(shipment.status.toString())} to ${FormatLocation(
          nextLocation
        )}`;
      }
      break;
    }

    case Status.Delivered: {
      // find last stop with an arrived value
      const revSeqNumSorted = [...shipment.stops].sort((s1, s2) => {
        return s2.sequenceNumber - s1.sequenceNumber;
      });
      const deliveredLocation = revSeqNumSorted.find(
        (s) => s.stopType === StopType.Drop && s.arrived
      )?.location;

      if (deliveredLocation) {
        return `${translate(shipment.status.toString())} to ${FormatLocation(
          deliveredLocation
        )}`;
      }
      break;
    }

    case Status.Booked:
    case Status.Ordered:
    case Status.None:
    default:
      break;
  }

  // Only return status
  return translate(shipment.status.toString());
};

export const DecimalValueOrDefault = (value: number): string => {
  return value >= 0
    ? toLocaleNumber(value, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : '-';
};

export const GetUserUnitOfMeasurePreference = () => {
  // Users are not logged in so cannot use getUserPreferences from shared package.
  // In the future possibly determine user preference from browser language setting.
  // standard or metric.
  return 'standard';
};

export * from './formatDateTime';

export const ValueOrDefault = (s: string, d = '-') => (s && s.trim() ? s.trim() : d);

export const LastKnownLocationOrDefault = (
  city?: string,
  state?: string,
  eventDateTime?: string
) => {
  if (city && state) {
    return `${city}, ${state} on ${eventDateTime ?? '-'}`;
  } else {
    return '';
  }
};

export const ConvertToMultiWordString = (s: string) => {
  if (s) {
    return s.replace(/([A-Z])/g, ' $1').trim();
  }
  return s;
};

export const ConvertStopTypes = (stopType: string): string => {
  if (stopType === StopType.Pick) {
    return 'Pickup From';
  } else if (stopType === StopType.Drop) {
    return 'Deliver To';
  }

  return ConvertToMultiWordString(stopType);
};
